import type { StateCreator } from 'zustand';
import { InspectionState } from 'components/common/dataGrid/columnsFactory/types';
import { EntityName } from 'shared/domain/entityName';
import { inspectionClient } from 'shared/domain/inspection/inspectionClient';
import type { InspectionModel } from 'shared/domain/inspection/inspectionModel';
import { createEntityCollectionActions } from 'store/entityCollection';
import type { DataActions } from 'store/types';
import type { InspectionStoreState } from '../../store';
import type { InspectionsCollection, InspectionsItems, InspectionsSlice } from './types';
import { updateDeletedInList, updateDeletedInSingleItem } from '../../helpers/updateDeletedState';
import { isSkippedDuringAtomicSave } from '../../utils/isSkippedDuringAtomicSave';

const EMPTY_INSPECTIONS: InspectionsItems = {
  items: [],
  total: 0,
};

const getDefaultState = () => ({
  inspections: EMPTY_INSPECTIONS,
  inspectionsLoading: true,
  inspectionListState: InspectionState.published,
});

type InspectionsSliceCreator = StateCreator<InspectionStoreState, [], [], InspectionsSlice>;

export const createInspectionsSlice: InspectionsSliceCreator = (set, get) => {
  const dataActions: DataActions<InspectionModel> = {
    setItems: (items) => get().setInspections({ items, total: items.length }),
    setLoading: (loading) => get().setInspectionsLoading(loading),
    reset: () => get().resetInspections(),
  };
  const inspectionsCollection: InspectionsCollection = {
    ...dataActions,
    ...createEntityCollectionActions(
      {
        client: inspectionClient,
        entityName: EntityName.inspections,
        // The list is read again when the atomic save ends (`setAtomicSaveEnabled`).
        skipReloadAfter: (event) => isSkippedDuringAtomicSave(event, get().atomicSaveEnabled),
      },
      () => dataActions
    ),
  };

  const changeDeleted = async (
    change: (inspection: { inspectionId: string; projectId: string }) => Promise<void>,
    inspectionId: string,
    projectId: string,
    deleted: boolean
  ): Promise<void> => {
    await change({ inspectionId, projectId });
    get().updateInspectionDeleted(inspectionId, deleted);
  };

  return {
    ...getDefaultState(),
    inspectionsCollection,
    setInspections: (inspections) =>
      set({
        inspections,
        inspectionsLoading: false,
      }),
    setInspectionsLoading: (inspectionsLoading) => set({ inspectionsLoading }),
    setInspectionListState: (inspectionListState) => set({ inspectionListState }),
    updateInspectionDeleted: (inspectionId, deleted) =>
      set((state) => ({
        inspections: {
          ...state.inspections,
          items: updateDeletedInList(state.inspections.items, inspectionId, deleted),
        },
        inspectionModel: updateDeletedInSingleItem(state.inspectionModel, inspectionId, deleted),
        inspection: updateDeletedInSingleItem(state.inspection, inspectionId, deleted),
      })),
    archiveInspection: (inspectionId, projectId) =>
      changeDeleted(inspectionClient.remove, inspectionId, projectId, true),
    restoreInspection: (inspectionId, projectId) =>
      changeDeleted(inspectionClient.restore, inspectionId, projectId, false),
    resetInspections: () => set(getDefaultState()),
  };
};

export type { InspectionsSlice, InspectionsItems } from './types';
